import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatINR } from "@/components/ChefCard";
import { ArrowRight, Home, Clock, Users, ShieldCheck, Check, Minus } from "lucide-react";
import DietaryBlock from "@/components/DietaryBlock";
import DishGallery from "@/components/DishGallery";

export default function ChefsTableDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { requireAuth } = useAuth();
  const [ev, setEv] = useState(null);
  const [seats, setSeats] = useState(2);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [dietary, setDietary] = useState({
    dietary_preferences: [], allergies: [], chef_notes: "", kitchen_equipment: [],
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/chefs-table/${id}`);
      setEv(r.data.event);
    })();
  }, [id]);

  if (!ev) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  const left = (ev.total_seats || 0) - (ev.seats_booked || 0);
  const onRequest = ev.price_on_request || !ev.cover_charge;
  const total = onRequest ? 0 : seats * (ev.cover_charge || 0);
  const soldOut = false;

  const submit = async () => {
    if (!requireAuth()) return;
    if (seats < 1) return toast.error("Tell us how many guests");
    setSubmitting(true);
    try {
      await api.post("/bookings/chefs-table", {
        event_id: ev.id,
        seats,
        contact_name: contactName,
        contact_phone: contactPhone,
        dietary_preferences: dietary.dietary_preferences,
        allergies: dietary.allergies,
        chef_notes: dietary.chef_notes,
      });
      toast.success(onRequest
        ? "Request received — our team will share the quote and confirm shortly."
        : "Seats reserved — pay online now or our team will confirm shortly.");
      nav("/my-bookings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reservation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="chefs-table-detail">
      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-3">
          <div className="aspect-[16/10] overflow-hidden bg-neutral-900 border border-white/10">
            <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center gap-3 mt-6 flex-wrap">
            <span className="chip text-[#F5B041]">At your home</span>
            {ev.featured && <span className="chip text-[#C04000] border-[#C04000]/40">Signature evening</span>}
            {ev.tags?.filter((t) => t.toLowerCase() !== "at your home").map((t) => <span key={t} className="chip">{t}</span>)}
          </div>
          <h1 className="font-serif-display text-5xl mt-6 leading-tight">{ev.title}</h1>
          <p className="font-script text-2xl text-[#F5B041] mt-2">{ev.tagline}</p>

          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            <div className="border border-white/10 p-4">
              <Home size={16} className="text-[#F5B041]" />
              <div className="small-caps text-[10px] text-neutral-500 mt-2">Where</div>
              <div className="text-sm">At your home · any city on request</div>
            </div>
            <div className="border border-white/10 p-4">
              <Clock size={16} className="text-[#F5B041]" />
              <div className="small-caps text-[10px] text-neutral-500 mt-2">Service</div>
              <div className="text-sm">{ev.event_time}</div>
            </div>
            <div className="border border-white/10 p-4">
              <Users size={16} className="text-[#F5B041]" />
              <div className="small-caps text-[10px] text-neutral-500 mt-2">Table size</div>
              <div className="text-sm" data-testid="seats-left">Up to {ev.total_seats} guests</div>
            </div>
          </div>

          <div className="dotted-sep my-8" />
          <p className="text-neutral-300 leading-relaxed text-lg">{ev.description}</p>
          <p className="text-neutral-500 text-sm mt-4">{ev.chef_credit}</p>

          {ev.courses?.length > 0 && (
            <div className="mt-12">
              <div className="small-caps text-neutral-500 mb-4">The Menu</div>
              <ol className="space-y-4">
                {ev.courses.map((c, i) => (
                  <li key={c} className="flex items-start gap-6 border-b border-white/5 pb-4">
                    <div className="font-serif-display text-3xl text-[#C04000] w-10">{String(i + 1).padStart(2, "0")}</div>
                    <div className="font-serif-menu text-xl pt-1">{c}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <DishGallery images={ev.gallery || []} title={ev.title} />

          {(ev.inclusions?.length > 0 || ev.exclusions?.length > 0) && (            <div className="mt-12 grid sm:grid-cols-2 gap-8" data-testid="table-inclusions">
              {ev.inclusions?.length > 0 && (
                <div>
                  <div className="small-caps text-neutral-500 mb-4">What's included</div>
                  <ul className="space-y-3">
                    {ev.inclusions.map((i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-neutral-300 leading-relaxed">
                        <Check size={14} className="text-[#F5B041] shrink-0 mt-1" /> {i}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {ev.exclusions?.length > 0 && (
                <div>
                  <div className="small-caps text-neutral-500 mb-4">Not included</div>
                  <ul className="space-y-3">
                    {ev.exclusions.map((i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-neutral-400 leading-relaxed">
                        <Minus size={14} className="text-neutral-600 shrink-0 mt-1" /> {i}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-14">
            <div className="small-caps text-neutral-500 mb-2">Tell us about your table</div>            <h3 className="font-serif-display text-3xl mb-2">Dietary & lifestyle</h3>
            <p className="text-neutral-400 text-sm mb-8 max-w-lg">Optional, but helps our chefs plate for your party.</p>
            <DietaryBlock value={dietary} onChange={setDietary} testIdPrefix="table" />
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="sticky top-24 space-y-6">
            <div className="bg-[#141414] border border-white/10 p-7">
              <div className="small-caps text-[10px] text-neutral-500">Per guest</div>
              {onRequest ? (
                <>
                  <div className="font-serif-display text-4xl mt-1" data-testid="table-price">Rates on request</div>
                  <p className="text-neutral-400 text-sm mt-2">
                    One all-inclusive price per guest — chef, service team, ingredients, plating and cleanup.
                    Share your date and guest count and we'll send the quote.
                  </p>
                </>
              ) : (
                <>
                  <div className="font-serif-display text-5xl mt-1" data-testid="table-price">₹{formatINR(ev.cover_charge)}</div>
                  <p className="text-neutral-400 text-sm mt-2">
                    Includes the full set menu, cooked and served at your home. Beverages billed separately.
                  </p>
                </>
              )}

              <div className="dotted-sep my-6" />

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Your name</Label>
                    <Input
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      data-testid="table-contact-name"
                      placeholder="Full name"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11 focus-visible:border-[#C04000] focus-visible:ring-0"
                    />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Contact number</Label>
                    <Input
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      data-testid="table-contact-phone"
                      placeholder="+91 98765 43210"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11 focus-visible:border-[#C04000] focus-visible:ring-0"
                    />
                  </div>
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Guests</Label>
                  <Input
                    type="number"
                    min={1}
                    value={seats}
                    onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value) || 1))}
                    data-testid="table-seats-input"
                    className="mt-2 rounded-none bg-transparent border-white/10 h-11 focus-visible:border-[#C04000] focus-visible:ring-0"
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border border-white/10 p-6">
              <div className="small-caps text-neutral-500 text-[10px]">Your evening</div>
              <div className="dotted-sep my-4" />
              {onRequest ? (
                <div className="text-sm space-y-2">
                  <div className="flex justify-between"><span className="text-neutral-400">Guests</span><span>{seats}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Price per guest</span><span className="text-[#F5B041]">On request</span></div>
                  <p className="text-[11px] text-neutral-500 pt-2">We'll confirm the all-inclusive quote once we know your date, city and menu tweaks.</p>
                </div>
              ) : (
                <>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">{seats} × ₹{formatINR(ev.cover_charge)}</span>
                      <span>₹{formatINR(total)}</span>
                    </div>
                  </div>
                  <div className="dotted-sep my-4" />
                  <div className="flex items-end justify-between">
                    <span className="small-caps text-[10px] text-neutral-500">Total</span>
                    <span className="font-serif-display text-3xl text-[#F5B041]">₹{formatINR(total)}</span>
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="reserve-table-btn"
              className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px] disabled:opacity-50"
            >
              {onRequest ? <>Request this table <ArrowRight size={14} className="ml-2" /></>
                : <>Reserve my table <ArrowRight size={14} className="ml-2" /></>}
            </Button>
            <div className="flex items-center gap-2 text-[10px] text-neutral-500">
              <ShieldCheck size={12} className="text-[#F5B041]" /> NDA-bound team · Setup and cleanup included
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
